'use client';

import { useState, useCallback, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import type { CityPin, CityBusinessData, CompetitorLocation, HailSpot } from './ExpansionMap';

// ─── Types ────────────────────────────────────────────────────────────────────
interface RevenueForecasts {
  conservative: string;
  moderate: string;
  aggressive: string;
  rationale: string;
}

interface MarketIntelligenceSummary {
  city: string;
  state: string;
  executiveSummary: string;
  competitivePositioning: string;
  revenueForecasts: RevenueForecasts;
  keyOpportunities: string[];
  keyRisks: string[];
  recommendedAction: string;
  confidenceScore: number;
  generatedAt: string;
}

interface MarketIntelligencePanelProps {
  cityPins: CityPin[];
  businessData: CityBusinessData[];
  competitorData: CompetitorLocation[];
  hailData: HailSpot[];
}

// ─── City Selector ────────────────────────────────────────────────────────────
interface CitySelectorProps {
  pins: CityPin[];
  selectedId: string | null;
  onSelect: (pin: CityPin) => void;
}

function CitySelector({ pins, selectedId, onSelect }: CitySelectorProps) {
  const STATUS_COLORS: Record<string, string> = {
    'active-market': '#10B981',
    'launching': '#8B5CF6',
    'target-market': '#E89A3C',
    'prospecting': '#F59E0B',
    'paused': '#22C55E',
    'competitor-held': '#EF4444',
  };

  const STATUS_EMOJI: Record<string, string> = {
    'active-market': '✅',
    'launching': '🚀',
    'target-market': '🎯',
    'prospecting': '🔍',
    'paused': '📈',
    'competitor-held': '🚫',
  };

  if (pins.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: '#F3F4F6' }}>
          <Icon name="MapPinIcon" size={24} style={{ color: '#9CA3AF' }} />
        </div>
        <p className="text-sm font-semibold" style={{ color: '#374151' }}>No city pins added yet</p>
        <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>Add cities to the map to generate market intelligence</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      {pins.map((pin) => {
        const color = STATUS_COLORS[pin.city_status] ?? '#6B7280';
        const emoji = STATUS_EMOJI[pin.city_status] ?? '📍';
        const isSelected = pin.id === selectedId;
        return (
          <button
            key={pin.id}
            onClick={() => onSelect(pin)}
            className="flex flex-col items-start p-3 rounded-xl text-left transition-all hover:scale-[1.02]"
            style={{
              background: isSelected ? `${color}15` : '#F9FAFB',
              border: `2px solid ${isSelected ? color : '#E5E7EB'}`,
              boxShadow: isSelected ? `0 2px 12px ${color}25` : 'none',
            }}
          >
            <span className="text-base mb-1">{emoji}</span>
            <span className="text-xs font-bold leading-tight" style={{ color: isSelected ? color : '#111827' }}>
              {pin.city_name}
            </span>
            <span className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>{pin.state}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Confidence Badge ─────────────────────────────────────────────────────────
function ConfidenceBadge({ score }: { score: number }) {
  const color = score >= 75 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';
  const label = score >= 75 ? 'High Confidence' : score >= 50 ? 'Moderate' : 'Low Confidence';
  return (
    <div
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
      style={{ background: `${color}15`, border: `1px solid ${color}40` }}
    >
      <div className="w-2 h-2 rounded-full" style={{ background: color }} />
      <span className="text-[10px] font-bold" style={{ color }}>{score}% {label}</span>
    </div>
  );
}

// ─── Revenue Forecast Card ────────────────────────────────────────────────────
function RevenueForecastCard({ forecasts }: { forecasts: RevenueForecasts }) {
  const scenarios = [
    { key: 'conservative', label: 'Conservative', value: forecasts.conservative, color: '#6B7280', bg: '#F9FAFB', icon: 'ArrowTrendingDownIcon' },
    { key: 'moderate', label: 'Base Case', value: forecasts.moderate, color: '#E89A3C', bg: '#EFF6FF', icon: 'ArrowTrendingUpIcon' },
    { key: 'aggressive', label: 'Aggressive', value: forecasts.aggressive, color: '#10B981', bg: '#ECFDF5', icon: 'RocketLaunchIcon' },
  ] as const;

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #E0E7FF' }}>
      <div className="px-4 py-3 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)' }}>
        <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #4338CA 100%)' }}>
          <Icon name="CurrencyDollarIcon" size={12} className="text-white" />
        </div>
        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#3730A3' }}>Revenue Forecasts</span>
      </div>
      <div className="p-3 grid grid-cols-3 gap-2">
        {scenarios.map((s) => (
          <div
            key={s.key}
            className="rounded-xl p-3 text-center"
            style={{ background: s.bg, border: `1px solid ${s.color}30` }}
          >
            <Icon name={s.icon as any} size={16} style={{ color: s.color, margin: '0 auto 6px' }} />
            <div className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: s.color }}>{s.label}</div>
            <div className="text-xs font-black leading-tight" style={{ color: '#111827' }}>{s.value}</div>
          </div>
        ))}
      </div>
      <div className="px-4 pb-3">
        <p className="text-[10px] leading-relaxed" style={{ color: '#6B7280' }}>
          <span className="font-semibold">Basis: </span>{forecasts.rationale}
        </p>
      </div>
    </div>
  );
}

// ─── Opportunities & Risks ────────────────────────────────────────────────────
function OpportunitiesRisks({ opportunities, risks }: { opportunities: string[]; risks: string[] }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {/* Opportunities */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #A7F3D0' }}>
        <div className="px-3 py-2 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' }}>
          <Icon name="ArrowTrendingUpIcon" size={12} style={{ color: '#059669' }} />
          <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#065F46' }}>Opportunities</span>
        </div>
        <div className="p-2 space-y-1.5">
          {opportunities.length > 0 ? opportunities.map((opp, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: '#ECFDF5', border: '1px solid #A7F3D0' }}>
                <span className="text-[8px] font-black" style={{ color: '#059669' }}>{i + 1}</span>
              </div>
              <p className="text-[10px] leading-relaxed" style={{ color: '#374151' }}>{opp}</p>
            </div>
          )) : (
            <p className="text-[10px] text-gray-400 p-1">No opportunities identified</p>
          )}
        </div>
      </div>

      {/* Risks */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #FECACA' }}>
        <div className="px-3 py-2 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)' }}>
          <Icon name="ExclamationTriangleIcon" size={12} style={{ color: '#DC2626' }} />
          <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#991B1B' }}>Key Risks</span>
        </div>
        <div className="p-2 space-y-1.5">
          {risks.length > 0 ? risks.map((risk, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
                <Icon name="ExclamationTriangleIcon" size={8} style={{ color: '#DC2626' }} />
              </div>
              <p className="text-[10px] leading-relaxed" style={{ color: '#374151' }}>{risk}</p>
            </div>
          )) : (
            <p className="text-[10px] text-gray-400 p-1">No risks identified</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Skeleton Loader ──────────────────────────────────────────────────────────
function IntelligenceSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <div className="h-5 rounded-lg" style={{ background: '#E5E7EB', width: '40%' }} />
        <div className="h-6 rounded-full" style={{ background: '#E5E7EB', width: '20%' }} />
      </div>
      {/* Summary skeleton */}
      <div className="rounded-xl p-4 space-y-2" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
        <div className="h-3 rounded-full" style={{ background: '#E5E7EB', width: '95%' }} />
        <div className="h-3 rounded-full" style={{ background: '#E5E7EB', width: '85%' }} />
        <div className="h-3 rounded-full" style={{ background: '#E5E7EB', width: '75%' }} />
      </div>
      {/* Forecast skeleton */}
      <div className="rounded-xl p-4" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
        <div className="h-3 rounded-full mb-3" style={{ background: '#E5E7EB', width: '30%' }} />
        <div className="grid grid-cols-3 gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl p-3" style={{ background: '#F3F4F6', height: 72 }} />
          ))}
        </div>
      </div>
      {/* Opportunities/Risks skeleton */}
      <div className="grid grid-cols-2 gap-3">
        {[0, 1].map((i) => (
          <div key={i} className="rounded-xl p-3 space-y-2" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', height: 100 }}>
            <div className="h-2.5 rounded-full" style={{ background: '#E5E7EB', width: '60%' }} />
            <div className="h-2.5 rounded-full" style={{ background: '#E5E7EB', width: '90%' }} />
            <div className="h-2.5 rounded-full" style={{ background: '#E5E7EB', width: '75%' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────
export default function MarketIntelligencePanel({
  cityPins,
  businessData,
  competitorData,
  hailData,
}: MarketIntelligencePanelProps) {
  const [selectedPin, setSelectedPin] = useState<CityPin | null>(null);
  const [intelligence, setIntelligence] = useState<MarketIntelligenceSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cacheRef = useRef<Record<string, MarketIntelligenceSummary>>({});

  const handleSelectCity = useCallback(async (pin: CityPin) => {
    setSelectedPin(pin);
    setError(null);

    // Check cache first
    const cacheKey = `${pin.city_name}_${pin.state}`;
    if (cacheRef.current[cacheKey]) {
      setIntelligence(cacheRef.current[cacheKey]);
      return;
    }

    setLoading(true);
    setIntelligence(null);

    try {
      // Gather live data context for this city
      const bizData = businessData.find(
        (d) => d.city.toLowerCase() === pin.city_name.toLowerCase()
      );
      const nearbyCompetitors = competitorData.filter((c) => {
        const latDiff = Math.abs(c.lat - pin.latitude);
        const lngDiff = Math.abs(c.lng - pin.longitude);
        return latDiff < 1.5 && lngDiff < 1.5;
      });
      const nearbyHail = hailData.filter((h) => {
        const latDiff = Math.abs(h.lat - pin.latitude);
        const lngDiff = Math.abs(h.lng - pin.longitude);
        return latDiff < 2 && lngDiff < 2;
      });

      const response = await fetch('/api/expansion/market-intelligence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          city: pin.city_name,
          state: pin.state,
          lat: pin.latitude,
          lng: pin.longitude,
          cityStatus: pin.city_status,
          jobCount: bizData?.jobCount,
          revenue: bizData?.revenue,
          competitorCount: nearbyCompetitors.length,
          hailEvents: nearbyHail.length > 0 ? nearbyHail.length * 3 : undefined,
        }),
      });

      const json = await response.json();
      if (!json.success) throw new Error(json.error ?? 'Failed to generate intelligence');

      const result: MarketIntelligenceSummary = json.data;
      cacheRef.current[cacheKey] = result;
      setIntelligence(result);
    } catch (err: any) {
      setError(err.message ?? 'Failed to generate market intelligence');
    } finally {
      setLoading(false);
    }
  }, [businessData, competitorData, hailData]);

  const handleRefresh = useCallback(() => {
    if (!selectedPin) return;
    const cacheKey = `${selectedPin.city_name}_${selectedPin.state}`;
    delete cacheRef.current[cacheKey];
    handleSelectCity(selectedPin);
  }, [selectedPin, handleSelectCity]);

  const formatTimestamp = (iso: string) => {
    try {
      return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: '#fff', border: '1px solid #E5E7EB', boxShadow: '0 2px 16px rgba(0,0,0,0.07)' }}
    >
      {/* ── Panel Header ── */}
      <div
        className="px-6 py-4 flex items-center justify-between"
        style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(232,154,60,0.2)' }}
          >
            <Icon name="SparklesIcon" size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white">Market Intelligence</h2>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Gemini AI · Competitive positioning &amp; revenue forecasts per city
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {intelligence && (
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all hover:bg-white/20 disabled:opacity-50"
              style={{ color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.2)' }}
              title="Regenerate analysis"
            >
              <Icon name="ArrowPathIcon" size={12} />
              Refresh
            </button>
          )}
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{ background: 'rgba(232,154,60,0.2)', border: '1px solid rgba(232,154,60,0.4)' }}
          >
            <div className="w-1.5 h-1.5 rounded-full bg-[#E89A3C] animate-pulse" />
            <span className="text-[10px] font-bold text-white">Gemini 2.5 Flash</span>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* ── City Selector ── */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Icon name="MapPinIcon" size={14} style={{ color: '#6B7280' }} />
            <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#6B7280' }}>
              Select a City to Analyze
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: '#EFF6FF', color: '#1C2E5E' }}>
              {cityPins.length} cities
            </span>
          </div>
          <CitySelector
            pins={cityPins}
            selectedId={selectedPin?.id ?? null}
            onSelect={handleSelectCity}
          />
        </div>

        {/* ── Intelligence Output ── */}
        {selectedPin && (
          <div className="space-y-4">
            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px" style={{ background: '#E5E7EB' }} />
              <span className="text-[10px] font-bold uppercase tracking-wider px-2" style={{ color: '#9CA3AF' }}>
                {selectedPin.city_name}, {selectedPin.state} — Intelligence Report
              </span>
              <div className="flex-1 h-px" style={{ background: '#E5E7EB' }} />
            </div>

            {/* Loading State */}
            {loading && <IntelligenceSkeleton />}

            {/* Error State */}
            {error && !loading && (
              <div
                className="rounded-xl p-4 flex items-start gap-3"
                style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}
              >
                <Icon name="ExclamationTriangleIcon" size={16} style={{ color: '#DC2626', flexShrink: 0, marginTop: 2 }} />
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#991B1B' }}>Analysis Failed</p>
                  <p className="text-xs mt-0.5" style={{ color: '#DC2626' }}>{error}</p>
                  <button
                    onClick={handleRefresh}
                    className="mt-2 text-xs font-semibold underline"
                    style={{ color: '#DC2626' }}
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}

            {/* Intelligence Report */}
            {intelligence && !loading && (
              <div className="space-y-4">
                {/* Report Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold" style={{ color: '#1C2E5E' }}>
                      {intelligence.city}, {intelligence.state}
                    </span>
                    <ConfidenceBadge score={intelligence.confidenceScore} />
                  </div>
                  <span className="text-[10px]" style={{ color: '#9CA3AF' }}>
                    Generated at {formatTimestamp(intelligence.generatedAt)}
                  </span>
                </div>

                {/* Executive Summary */}
                <div
                  className="rounded-xl p-4"
                  style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', border: '1px solid #DBEAFE' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-5 h-5 rounded-md flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}
                    >
                      <Icon name="DocumentTextIcon" size={10} className="text-white" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#1C2E5E' }}>
                      Executive Summary
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: '#1E3A5F' }}>
                    {intelligence.executiveSummary}
                  </p>
                </div>

                {/* Competitive Positioning */}
                <div
                  className="rounded-xl p-4"
                  style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FEF3C7 100%)', border: '1px solid #FDE68A' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-5 h-5 rounded-md flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}
                    >
                      <Icon name="FlagIcon" size={10} className="text-white" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#92400E' }}>
                      Competitive Positioning
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: '#78350F' }}>
                    {intelligence.competitivePositioning}
                  </p>
                </div>

                {/* Revenue Forecasts */}
                <RevenueForecastCard forecasts={intelligence.revenueForecasts} />

                {/* Opportunities & Risks */}
                <OpportunitiesRisks
                  opportunities={intelligence.keyOpportunities}
                  risks={intelligence.keyRisks}
                />

                {/* Recommended Action */}
                <div
                  className="rounded-xl p-4 flex items-start gap-3"
                  style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)' }}
                >
                  <div
                    className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(232,154,60,0.25)' }}
                  >
                    <Icon name="BoltIcon" size={16} className="text-white" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white opacity-70 block mb-1">
                      Recommended Action
                    </span>
                    <p className="text-sm font-semibold text-white leading-snug">
                      {intelligence.recommendedAction}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty state when no city selected */}
        {!selectedPin && cityPins.length > 0 && (
          <div
            className="rounded-xl p-6 text-center"
            style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', border: '1px dashed #BFDBFE' }}
          >
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3"
              style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}
            >
              <Icon name="SparklesIcon" size={22} className="text-white" />
            </div>
            <p className="text-sm font-bold" style={{ color: '#1C2E5E' }}>Select a city above</p>
            <p className="text-xs mt-1 leading-relaxed" style={{ color: '#6B7280' }}>
              Gemini will analyze live business data, competitor density, and storm history to generate a full market intelligence report with revenue forecasts.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
